import { useCallback, useEffect, useRef, useState } from "react";
import { getImportJob } from "../api/transactions";
import {
  RUNNING_JOB_STATUSES,
  type ImportJobStatus,
  type ImportResult,
} from "../types/transaction";
import { asRefusal } from "./useServerAnswer";

// The wait between two reads of a running job.
export const POLL_INTERVAL_MS = 1500;
// A failed read is tried again this many times, this far apart, before its error shows.
export const READ_RETRIES = 3;
export const RETRY_WAIT_MS = 2000;

export interface ImportJobPoll {
  /** Start polling the upload's job when its categorization is still running. */
  follow: (result: ImportResult) => void;
  /** The server's sentence when a status read was refused, else "". */
  refusal: string;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Read the job once, trying a failed read again up to READ_RETRIES times,
 * RETRY_WAIT_MS apart (a network blip or a restart's 502). `stale()` stops
 * the waiting early when a newer poll took over. The last failure is thrown.
 */
async function readJob(
  clientId: number,
  jobId: string,
  stale: () => boolean,
): Promise<ImportJobStatus | null> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await getImportJob(clientId, jobId);
    } catch (err) {
      if (attempt >= READ_RETRIES) throw err;
      await wait(RETRY_WAIT_MS);
      if (stale()) return null;
    }
  }
}

/**
 * Poll `GET /bank/imports/{job_id}` until the categorization is no longer
 * queued or in progress, then call `onFinished` once (the review list
 * reloads). The first read is immediate; a read that still fails after its
 * retries (readJob) stops the polling and keeps the server's sentence. A newer upload or unmounting stops an
 * older poll (its generation is no longer the current one).
 */
export function useImportJobPoll(clientId: number, onFinished: () => void): ImportJobPoll {
  const [refusal, setRefusal] = useState("");
  const generation = useRef(0);
  const finished = useRef(onFinished);

  useEffect(() => {
    finished.current = onFinished;
  }, [onFinished]);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const poll = useCallback(
    async (jobId: string, own: number) => {
      const stale = (): boolean => own !== generation.current;
      try {
        for (;;) {
          const job = await readJob(clientId, jobId, stale);
          if (job === null || stale()) return;
          if (!RUNNING_JOB_STATUSES.includes(job.categorization.status)) break;
          await wait(POLL_INTERVAL_MS);
          if (own !== generation.current) return;
        }
        finished.current();
      } catch (err) {
        if (own === generation.current) setRefusal(asRefusal(err).message);
      }
    },
    [clientId],
  );

  const follow = useCallback(
    (result: ImportResult) => {
      generation.current += 1;
      setRefusal("");
      const status = result.categorization_status ?? "";
      if (result.job_id && RUNNING_JOB_STATUSES.includes(status)) {
        void poll(result.job_id, generation.current);
      }
    },
    [poll],
  );

  return { follow, refusal };
}
